import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ToastContainer, toast } from "react-toastify";

import {
  resetLoginData,
  resetPasswordThunk,
} from "../../redux/features/loginSlice";

const ResetPassword = () => {
  const location = useLocation();
  const login = useSelector((state) => state.login);
  const searchParams = new URLSearchParams(location.search);
  const appUserId = searchParams.get("appUserId");
  const jwtToken = searchParams.get("t");
  let navigate = useNavigate();
  const dispatch = useDispatch();

  //local state
  const [isLoading, setIsLoading] = useState(false);
  const [isCapsLockOn, setIsCapsLockOn] = useState(false);
  const [error, setError] = useState(false);

  // remove logged in user data data
  useEffect(() => {
    dispatch(resetLoginData());
  }, []);

  //Check for Caps Lock
  const handleKeyUp = (event) => {
    if (event.getModifierState("CapsLock")) {
      setIsCapsLockOn(true);
    } else {
      setIsCapsLockOn(false);
    }
  };

  //schema
  const passwordValidationSchema = Yup.object({
    currentPassword: Yup.string().required("Current Password is required"),
    newPassword: Yup.string()
      .required("New Password is required")
      .min(8, "Password must be at least 8 characters long")
      .test(
        "password-strength",
        "The new password does not meet all password requirements. Please enter a password that meets all requirements listed below",
        (value) => {
          let count = 0;
          if (/[A-Z]/.test(value)) count++; // Check for upper case
          if (/[a-z]/.test(value)) count++; // Check for lower case
          if (/\d/.test(value)) count++; // Check for digit
          if (/[\W_]/.test(value)) count++; // Check for special character (non-word characters and underscores)
          return count >= 3; // Password must meet at least three of the criteria
        }
      )
      .test(
        "not-same-as-current",
        "The new/confirm password must be different from the existing password",
        function (value) {
          return this.parent.currentPassword !== value;
        }
      ),
    confirmNewPassword: Yup.string()
      .oneOf(
        [Yup.ref("newPassword"), null],
        "New password and confirm password do not match"
      )
      .required("Confirm Password is required"),
  });

  const formik = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    },
    validationSchema: passwordValidationSchema,
    onSubmit: (values) => {
      setIsLoading(true);
      setError("");
      dispatch(
        resetPasswordThunk({
          ...values,
          appUserId: +appUserId,
          jwtToken,
        })
      )
        .then((resp) => {
          setIsLoading(false);
          const result = resp.payload;
          if (result?.status === "fail" && result?.errorMessage) {
            setError(result?.errorMessage);
            toast.error(result?.errorMessage);
          } else if (result.status === "success") {
            setError("");
            toast.success(result?.responseBody);
            setTimeout(() => {
              // navigate("/login");
              navigate("/passwordChangeSuccessful");
            }, 4000);
          }
        })
        .catch(() => {
          setIsLoading(false);
        });
    },
  });

  const { adminData } = useSelector((state) => state.support);
  const [footerData, setFooterData] = useState({});

  useEffect(() => {
    if (adminData?.headerLinks?.siteFooter) {
      setFooterData({ ...adminData.headerLinks.siteFooter });
    }
  }, [adminData]);
  return (
    <main role="main" className="main" style={{ height: "100vh" }}>
      <div style={{ paddingTop: "20px" }}>
        <ToastContainer />
        <div className="forgot-header">
          <div className="container">
            <div className="group">
              <div className="span3">
                <div className="header-logo"></div>
              </div>
            </div>
          </div>
        </div>
        <article className="forgot-card shadow-1 center2">
          <div
            className="forgot-content"
            style={{ height: "auto", padding: "100px 50px 0px 50px" }}
          >
            <div style={{ display: "none" }} className="logo mobile-show"></div>
            <div>
              <center>
                <i style={{ fontSize: "45px" }} className="fa fa-lock"></i>
                <h1>Reset Password</h1>
                <p className="margin" style={{ marginBottom: "20px" }}>
                  Enter Current, New and Confirm password below and
                  <br />
                  select “Reset Password” to save it.
                </p>
              </center>
            </div>

            <form onSubmit={formik.handleSubmit}>
              <div className="forgot-fields" style={{ maxWidth: "80%" }}>
                <input
                  value={formik.values.currentPassword}
                  onChange={(e) => {
                    formik.handleChange(e);
                    error && setError("");
                  }}
                  onBlur={formik.handleBlur}
                  className="login-input"
                  placeholder="Current Password"
                  type="password"
                  name="currentPassword"
                  autoComplete="off"
                  onKeyUp={handleKeyUp}
                />
                {formik.touched.currentPassword &&
                formik.errors.currentPassword ? (
                  <div className="error">{formik.errors.currentPassword}</div>
                ) : null}
                {error ? <div className="error">{error}</div> : ""}
              </div>
              <div className="forgot-fields" style={{ maxWidth: "80%" }}>
                <input
                  value={formik.values.newPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="login-input"
                  placeholder="New Password"
                  type="password"
                  name="newPassword"
                  autoComplete="off"
                  onKeyUp={handleKeyUp}
                />
                {formik.touched.newPassword && formik.errors.newPassword ? (
                  <div className="error">{formik.errors.newPassword}</div>
                ) : null}
              </div>
              <div className="forgot-fields" style={{ maxWidth: "80%" }}>
                <input
                  value={formik.values.confirmNewPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="login-input"
                  placeholder="Confirm New Password"
                  type="password"
                  name="confirmNewPassword"
                  autoComplete="off"
                  onKeyUp={handleKeyUp}
                />
                {formik.touched.confirmNewPassword &&
                formik.errors.confirmNewPassword ? (
                  <div className="error">
                    {formik.errors.confirmNewPassword}
                  </div>
                ) : null}
              </div>
              {isCapsLockOn && (
                <div style={{ color: "red", marginTop: "10px" }}>
                  Warning: CapsLock is On
                </div>
              )}
              <button
                type="submit"
                className="margin-top1 btn btn-primary text-center"
                disabled={
                  isLoading ||
                  !formik.isValid ||
                  !formik.dirty ||
                  !formik.values.currentPassword ||
                  !formik.values.newPassword ||
                  !formik.values.confirmNewPassword
                }
              >
                {isLoading ? "Please wait " : "Reset Password"}
                {isLoading && (
                  <i className="fa fa-spinner fa-pulse fa-lg fa-fw"></i>
                )}
                {/* {isLoading
                  ? `Please wait ${(
                      <i className="fa fa-spinner fa-pulse fa-lg fa-fw"></i>
                    )}`
                  : "Reset Password"} */}
              </button>
            </form>
            <div className="pass-li">
              <ul>
                <li>Passwords must be at least 8 characters long.</li>
                <li>Passwords are case sensitive.</li>
                <li>
                  Passwords must contain at least three of the following: Upper
                  Case Character, Lower Case Character, Numeric Value, Special
                  Character.
                </li>
              </ul>
            </div>
            <div className="login-footer" style={{ marginTop: 0 }}>
              <br />

              <div className="margin-top3">
                <footer style={{ marginTop: 0 }}>
                  <div
                    className="container"
                    dangerouslySetInnerHTML={{
                      __html:
                        footerData?.clientSpecificLabel ||
                        footerData?.defaultLabel,
                    }}
                  ></div>
                </footer>
              </div>
            </div>
          </div>
        </article>
      </div>
    </main>
  );
};

export default ResetPassword;
